"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.launchTargetString = exports.LAUNCH_TARGET_PROBLEM_MESSAGES = exports.LAUNCH_LINK_EXAMPLES = exports.LAUNCHER_FAMILY_LABELS = exports.LAUNCHER_FAMILIES = exports.LAUNCHER_LABELS = exports.LAUNCHER_IDS = void 0;
exports.normalizeLaunchTarget = normalizeLaunchTarget;
exports.parseLaunchTarget = parseLaunchTarget;
exports.isValidLaunchTarget = isValidLaunchTarget;
exports.derivedCoverUrl = derivedCoverUrl;
const zod_1 = require("zod");
/**
 * What a game's "path" may be (owner, 2026-09-09).
 *
 * A title the venue installed itself is a FILE on the PC — the game's .exe,
 * or a shortcut the installer left behind. A title that lives in a launcher
 * (Steam, Epic, the Microsoft Store / Xbox app, Battle.net, Ubisoft Connect,
 * the EA app, Riot) is a LINK: the launcher's own URI, or the store page the
 * owner can copy from a browser. Both are typed into the same field, so ONE
 * parser decides what a string is, and it runs in three places with one
 * answer: the staff app (to say "Steam · app 730" under the field as it is
 * typed), the API (to refuse a path or link no PC could ever open), and the
 * agent on the PC (to choose how to launch it, and to refuse anything that
 * did not come through the other two — the catalogue is configuration, but a
 * catalogue from a cache someone hand-edited still goes through here).
 *
 * Links are an ALLOWLIST. A URI is handed to the operating system's protocol
 * handler, and an arbitrary scheme would let a mistyped entry open anything
 * registered on the machine; an unknown https link is refused rather than
 * opened in a browser on a customer's screen.
 */
/** The launchers a link may name. */
exports.LAUNCHER_IDS = [
    'steam',
    'epic',
    'microsoft-store',
    'battlenet',
    'ubisoft',
    'ea',
    'rockstar',
    'minecraft',
    'riot',
    'xbox',
];
/** Display names — proper nouns, the same in every language. */
exports.LAUNCHER_LABELS = {
    steam: 'Steam',
    epic: 'Epic Games',
    'microsoft-store': 'Microsoft Store / Xbox',
    battlenet: 'Battle.net',
    ubisoft: 'Ubisoft Connect',
    ea: 'EA app',
    rockstar: 'Rockstar Games Launcher',
    minecraft: 'Minecraft Launcher',
    riot: 'Riot Games',
    xbox: 'Xbox app',
};
/**
 * The launchers an ACCOUNT signs into (owner, 2026-10-05), which is coarser
 * than {@link LauncherId}. The Microsoft Store and the Xbox app sign in with
 * the same Microsoft account, so they are one family; every other family is
 * one launcher. The portal's "Login using my account" route, the per-PC Steam
 * account choice and the staff board's "sign the venue account back in"
 * notice are keyed on these. The API folds free-text Company columns and
 * launcher App names onto them (`game-account-launcher.ts`).
 */
exports.LAUNCHER_FAMILIES = [
    'steam',
    'epic',
    'microsoft',
    'battlenet',
    'ubisoft',
    'ea',
    'rockstar',
    'minecraft',
    'riot',
];
/** Display names — proper nouns, the same in every language. */
exports.LAUNCHER_FAMILY_LABELS = {
    steam: 'Steam',
    epic: 'Epic Games',
    microsoft: 'Microsoft Store',
    battlenet: 'Battle.net',
    ubisoft: 'Ubisoft Connect',
    ea: 'EA app',
    rockstar: 'Rockstar Games',
    minecraft: 'Minecraft',
    riot: 'Riot Client',
};
/** One example per launcher, for the hint under the field. */
exports.LAUNCH_LINK_EXAMPLES = [
    { launcher: 'steam', example: 'steam://rungameid/730' },
    { launcher: 'microsoft-store', example: 'https://apps.microsoft.com/detail/9NBLGGH4R315' },
    { launcher: 'xbox', example: 'ms-xbl-2d2bf0cc:' },
    { launcher: 'epic', example: 'com.epicgames.launcher://apps/Fortnite?action=launch&silent=true' },
    { launcher: 'riot', example: 'riot://valorant' },
    { launcher: 'battlenet', example: 'battlenet://VIPR' },
    { launcher: 'ubisoft', example: 'uplay://launch/635/0' },
    { launcher: 'ea', example: 'origin2://game/launch?offerIds=Origin.OFR.50.0002694' },
];
/** Anything before a colon that is longer than a drive letter. */
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;
/** `C:\…`, `C:/…`, `\\server\share\…` or a POSIX `/…`. */
const ABSOLUTE_PATH = /^(?:[a-zA-Z]:[\\/]|\\\\[^\\/]+[\\/]|\/)/;
/** Store product ids: twelve characters starting with 9, e.g. 9NBLGGH4R315. */
const STORE_PRODUCT = /\b(9[A-Z0-9]{11})\b/i;
const SHORTCUT = /\.(lnk|url)$/i;
/**
 * A store page typed without its `https://` — `apps.microsoft.com/detail/…`,
 * `store.steampowered.com/app/730` — is still that page. Only the hosts this
 * understands, so a bare `game.exe` stays "not a full path" rather than
 * becoming a web address nobody meant.
 */
const BARE_STORE_HOST = /^(?:[a-z0-9-]+\.)*(?:steampowered\.com|steamcommunity\.com|microsoft\.com|xbox\.com)(?:[/?#]|$)/i;
/** The schemes whose mistyped punctuation is worth repairing. */
const KNOWN_SCHEMES = new Set([
    'steam',
    'com.epicgames.launcher',
    'battlenet',
    'uplay',
    'ubisoft',
    'origin',
    'origin2',
    'link2ea',
    'rockstar',
    'minecraft',
    'riot',
    'ms-windows-store',
    'http',
    'https',
]);
/** A scheme name followed by SOME mixture of `:`, `/` and `\` — the `://` a person meant. */
const MISTYPED_SCHEME = /^([a-z][a-z0-9+.-]*)\s*([:/\\]{1,4})\s*/i;
/**
 * The URL inside the desktop shortcut the Xbox app makes for a game:
 * `ms-xbl-2d2bf0cc:` — its own scheme per title, nothing after the colon.
 * Right-click the shortcut, Properties, and that is the URL shown.
 */
const XBOX_SCHEME = /^ms-xbl-[a-z0-9]+$/i;
/**
 * The value as it should be stored: trimmed, and with a launcher's
 * punctuation put right. `steam//:rungameid/1938090` is a Steam link with
 * the colon in the wrong place (the owner's own slip, 2026-09-10), as are
 * `steam:/rungameid/…` and `steam:\rungameid\…`; each becomes
 * `steam://rungameid/1938090`. Only the schemes this knows are touched, so
 * `C:\Games\game.exe` — a one-letter "scheme" — is left exactly as typed.
 */
function normalizeLaunchTarget(raw) {
    const value = raw.trim();
    const match = MISTYPED_SCHEME.exec(value);
    if (!match || match[1] === undefined)
        return value;
    const scheme = match[1].toLowerCase();
    const rest = value.slice(match[0].length).replace(/\\/g, '/');
    // An Xbox shortcut URL ends at its colon; a slash or two after it is the
    // same link, anything else is left for the parser to refuse.
    if (XBOX_SCHEME.test(scheme))
        return rest.length === 0 ? `${scheme}:` : `${scheme}://${rest}`;
    if (!KNOWN_SCHEMES.has(scheme))
        return value;
    return `${scheme}://${rest}`;
}
function parseLaunchTarget(raw) {
    const value = normalizeLaunchTarget(raw);
    if (value.length === 0)
        return { kind: 'invalid', problem: 'empty' };
    // eslint-disable-next-line no-control-regex -- deliberate: reject control chars.
    if (/[\u0000-\u001f"`<>|^]/.test(value))
        return { kind: 'invalid', problem: 'bad_characters' };
    const scheme = SCHEME.exec(value);
    if (scheme && scheme[1] !== undefined && scheme[1].length > 1) {
        return parseLink(scheme[1].toLowerCase(), value);
    }
    if (BARE_STORE_HOST.test(value))
        return parseWebLink(`https://${value}`);
    if (!ABSOLUTE_PATH.test(value))
        return { kind: 'invalid', problem: 'relative_path' };
    return { kind: 'file', path: value, shortcut: SHORTCUT.test(value) };
}
function parseLink(scheme, value) {
    if (XBOX_SCHEME.test(scheme)) {
        // The Xbox app registers one handler per game; the OS opens it. Only the
        // bare form (or `://` after it) is a link — no tail may ride along.
        if (!/^ms-xbl-[a-z0-9]+:(?:\/\/)?$/i.test(value))
            return { kind: 'invalid', problem: 'bad_link' };
        return { kind: 'link', launcher: 'xbox', uri: `${scheme}:`, ref: scheme.slice('ms-xbl-'.length) };
    }
    switch (scheme) {
        case 'steam': {
            const match = /^steam:\/\/(?:rungameid|run|launch)\/(\d+)(?:\/.*)?$/i.exec(value);
            // `steam://open/library` and the like are Steam's, but name no game.
            if (!match)
                return { kind: 'invalid', problem: 'missing_id' };
            return steam(match[1]);
        }
        case 'http':
        case 'https':
            return parseWebLink(value);
        case 'ms-windows-store': {
            const product = STORE_PRODUCT.exec(value);
            return product ? store(product[1]) : { kind: 'invalid', problem: 'missing_id' };
        }
        // The launchers below are passed through as typed, so their whole link
        // is held to a closed alphabet and anchored at both ends: what the PC
        // hands to the OS is exactly what matched here, never a tail after it.
        case 'com.epicgames.launcher': {
            const match = /^com\.epicgames\.launcher:\/\/apps\/([A-Za-z0-9._%:-]+)(?:\?[A-Za-z0-9=&._%-]*)?$/i.exec(value);
            if (!match)
                return epicOrBad(value);
            return { kind: 'link', launcher: 'epic', uri: value, ref: safeDecode(match[1]) };
        }
        case 'battlenet': {
            const match = /^battlenet:\/\/([A-Za-z0-9]+)\/?$/.exec(value);
            if (!match)
                return linkProblem(value, /^battlenet:\/\/[A-Za-z0-9]/);
            return { kind: 'link', launcher: 'battlenet', uri: value, ref: match[1] };
        }
        case 'uplay':
        case 'ubisoft': {
            const match = /^(?:uplay|ubisoft):\/\/launch\/(\d+)(?:\/\d+)?\/?$/i.exec(value);
            if (!match)
                return linkProblem(value, /^(?:uplay|ubisoft):\/\/launch\/\d/i);
            return { kind: 'link', launcher: 'ubisoft', uri: value, ref: match[1] };
        }
        case 'origin':
        case 'origin2':
        case 'link2ea': {
            const match = /^(?:origin|link2ea):\/\/launchgame\/([A-Za-z0-9.:_-]+)(?:\?[A-Za-z0-9=&._-]*)?$/i.exec(value) ?? /^origin2:\/\/game\/launch\?offerIds=([A-Za-z0-9.,:_-]+)(?:&[A-Za-z0-9=&._-]*)?$/i.exec(value);
            if (!match) {
                return linkProblem(value, /^(?:(?:origin|link2ea):\/\/launchgame\/[A-Za-z0-9]|origin2:\/\/game\/launch\?offerIds=[A-Za-z0-9])/i);
            }
            return { kind: 'link', launcher: 'ea', uri: value, ref: safeDecode(match[1]) };
        }
        case 'rockstar': {
            const match = /^rockstar:\/\/([A-Za-z0-9\/._-]+)$/i.exec(value);
            if (!match)
                return linkProblem(value, /^rockstar:\/\/[A-Za-z0-9]/i);
            return { kind: 'link', launcher: 'rockstar', uri: value, ref: match[1] };
        }
        case 'minecraft': {
            const match = /^minecraft:(?:\/\/)?([A-Za-z0-9\/._?=&-]*)$/i.exec(value);
            if (!match)
                return { kind: 'invalid', problem: 'bad_link' };
            return { kind: 'link', launcher: 'minecraft', uri: value, ref: match[1] || 'launcher' };
        }
        case 'riot': {
            // Our own shorthand: the product Riot's client is told to launch —
            // `riot://valorant`, `riot://league_of_legends`. The PC turns it into
            // RiotClientServices.exe --launch-product=<product>.
            const match = /^riot:\/\/([a-z][a-z0-9_]*)\/?$/i.exec(value);
            if (!match)
                return { kind: 'invalid', problem: 'missing_id' };
            const product = match[1].toLowerCase();
            return { kind: 'link', launcher: 'riot', uri: `riot://${product}`, ref: product };
        }
        default:
            return { kind: 'invalid', problem: 'unknown_scheme' };
    }
}
/**
 * A link that names its launcher but did not match: `missing_id` when the
 * id itself is absent, `bad_link` when something is there but the link
 * carries a character or a tail the launcher's own handler would not take.
 */
function linkProblem(value, hasId) {
    return { kind: 'invalid', problem: hasId.test(value) ? 'bad_link' : 'missing_id' };
}
function epicOrBad(value) {
    return linkProblem(value, /^com\.epicgames\.launcher:\/\/apps\/[A-Za-z0-9]/i);
}
/** The percent-decoded id for display; the raw segment when it is not valid UTF-8 percent-encoding. */
function safeDecode(segment) {
    try {
        return decodeURIComponent(segment);
    }
    catch {
        return segment;
    }
}
function steam(appId) {
    return { kind: 'link', launcher: 'steam', uri: `steam://rungameid/${appId}`, ref: appId };
}
function store(productId) {
    const id = productId.toUpperCase();
    return {
        kind: 'link',
        launcher: 'microsoft-store',
        uri: `ms-windows-store://pdp/?productid=${id}`,
        ref: id,
    };
}
/**
 * The store pages a person can copy from a browser: a Steam store page
 * becomes the Steam run link; a Microsoft Store, microsoft.com or xbox.com
 * page becomes the Store product. Anything else on the web is refused —
 * there is no way to start a game from an Epic or Riot web page, and opening
 * a browser on a customer's screen is not a launch.
 */
function parseWebLink(value) {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return { kind: 'invalid', problem: 'unknown_link' };
    }
    const host = url.hostname.toLowerCase();
    if (host === 'store.steampowered.com' || host === 'steamcommunity.com') {
        const match = /\/app\/(\d+)/.exec(url.pathname);
        return match ? steam(match[1]) : { kind: 'invalid', problem: 'missing_id' };
    }
    if (host === 'apps.microsoft.com' ||
        host === 'www.microsoft.com' ||
        host === 'microsoft.com' ||
        host === 'www.xbox.com' ||
        host === 'xbox.com') {
        const product = STORE_PRODUCT.exec(url.pathname) ?? STORE_PRODUCT.exec(url.search);
        return product ? store(product[1]) : { kind: 'invalid', problem: 'missing_id' };
    }
    return { kind: 'invalid', problem: 'unknown_link' };
}
function isValidLaunchTarget(raw) {
    return parseLaunchTarget(raw).kind !== 'invalid';
}
/** What to tell the person who typed it. Keys the staff app translates; the API sends the English. */
exports.LAUNCH_TARGET_PROBLEM_MESSAGES = {
    empty: 'Enter a path or a launch link.',
    bad_characters: 'Double quotes, backticks, < > | ^ and control characters are not allowed.',
    relative_path: 'Use the full path, starting with the drive letter (C:\\Games\\game.exe).',
    unknown_scheme: 'Unknown launcher link. Use steam://, com.epicgames.launcher://, battlenet://, uplay://, origin://, riot:// or a Microsoft Store link.',
    unknown_link: 'Only Steam store and Microsoft Store / Xbox pages are understood. For other launchers use their launch link, or a shortcut file on the PC.',
    missing_id: 'That link does not say which game — it is missing the game id.',
    bad_link: 'That link is not in a form its launcher can open — check it against the example.',
};
/**
 * A path-or-link field as the API accepts it. Blank means "none" and is left
 * to the caller (`.optional().nullable()`, or `min(1)` where one is required).
 */
exports.launchTargetString = zod_1.z
    .string()
    .trim()
    .max(500)
    .superRefine((value, ctx) => {
    if (value.length === 0)
        return;
    const parsed = parseLaunchTarget(value);
    if (parsed.kind === 'invalid') {
        ctx.addIssue({ code: 'custom', message: exports.LAUNCH_TARGET_PROBLEM_MESSAGES[parsed.problem] });
    }
})
    // Stored with its punctuation put right, so a PC whose agent has not
    // learned the repair yet still gets a link it understands.
    .transform((value) => normalizeLaunchTarget(value));
/**
 * COVER ART FOR FREE, FROM THE LAUNCH LINK.
 *
 * Covers are uploaded by hand, one game at a time, which means a real venue's
 * library is mostly tiles with no art — and the Games grid is the screen the
 * customer stares at most (owner, 2026-09-13: the reference product is
 * wall-to-wall box art and ours was not).
 *
 * But we already know which Steam title a game IS, because the owner typed
 * `steam://rungameid/730` into the path field. Steam publishes the portrait
 * library capsule for every app at a predictable URL, and it is exactly the
 * 2:3 the tile wants. So a Steam title gets its real box art with nobody
 * uploading anything.
 *
 * TWO GUARDS, both deliberate:
 *
 * 1. STEAM ONLY. Every other launcher's ref is a product slug or an offer id
 *    with no public art endpoint, and guessing a URL that 404s is worse than
 *    admitting we have no picture — the tile has a good fallback.
 * 2. A PLAIN APP ID. `rungameid` is usually the app id, but for a mod or a
 *    non-Steam shortcut it is a packed 64-bit value (…3925868544) that names
 *    no capsule at all. Anything longer than seven digits is therefore not an
 *    app id and gets nothing.
 *
 * This is a GUESS, not a fact: the URL is returned without being fetched, so
 * a title whose capsule does not exist returns a URL that 404s. Every surface
 * that renders it must fall back on the image's error event rather than
 * showing a broken frame.
 */
const STEAM_APP_ID = /^[0-9]{1,7}$/;
function derivedCoverUrl(rawTarget) {
    if (!rawTarget)
        return null;
    const target = parseLaunchTarget(rawTarget);
    if (target.kind !== 'link' || target.launcher !== 'steam')
        return null;
    if (!STEAM_APP_ID.test(target.ref))
        return null;
    return `https://cdn.cloudflare.steamstatic.com/steam/apps/${target.ref}/library_600x900.jpg`;
}
//# sourceMappingURL=launch-target.js.map